import { useCallback, useMemo, useState } from 'react';
import type {
    FlaggedSpecialVendor,
    InvoicingSettings,
    InvoicingSettingsUpdatePayload,
    LocationDefaultTaxRate,
} from '../../../../hooks/useGetSettings';
import { invoicingSettingsForApi } from '../../../../utils/invoicingSettingsApi';
import { useSettingsDraft } from '../../SettingsDraftContext';

export const DEFAULT_RECEIVED_PERCENT_THRESHOLD = 0.5;
export const DEFAULT_RETURNED_PERCENT_THRESHOLD = 0.5;
export const DEFAULT_RECEIVED_BASE_THRESHOLD = 50;
export const DEFAULT_RETURNED_BASE_THRESHOLD = 50;

export const decimalToWholePercent = (value: number): number => Math.round(value * 100);
export const wholePercentToDecimal = (value: number): number => value / 100;

/** Tax rates allow fractional percents (e.g. 8.25%), so keep 3 decimal places of the percent. */
export const taxRateDecimalToPercent = (value: number): number => Math.round(value * 100000) / 1000;
export const percentToTaxRateDecimal = (value: number): number => Math.round(value * 1000) / 100000;

export const formatCurrency = (value: number): string =>
    value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

export const RECEIVED_AMOUNT_THRESHOLD_DESCRIPTION =
    'Applies to received and sublet invoices. If either total is under the first dollar amount, flag when the difference is this percentage or more. Otherwise flag when the difference is the second dollar amount or more.';

export const CREDITED_AMOUNT_THRESHOLD_DESCRIPTION =
    'Applies to credited invoices. If either total is under the first dollar amount, flag when the difference is this percentage or more. Otherwise flag when the difference is the second dollar amount or more.';

/**
 * Absolute difference used once a line is at or above the switchover.
 * An unset difference keeps the organization's current switchover amount.
 */
export const amountMismatchDollarThreshold = (
    dollar: number | null | undefined,
    baseDollar: number | null | undefined,
    fallback = DEFAULT_RECEIVED_BASE_THRESHOLD,
): number => dollar ?? baseDollar ?? fallback;

/** Tooltip for the switchover, percent, and difference threshold fields. */
export const amountMismatchThresholdHelp = (
    baseDollar: number,
    percent: number,
    dollar: number,
    flagBothDirections = true,
): string => {
    const pct = decimalToWholePercent(percent);
    const baseLabel = formatCurrency(baseDollar);
    const dollarLabel = formatCurrency(dollar);
    const direction = flagBothDirections
        ? 'Overcharges and undercharges both flag.'
        : 'Only a higher invoice total flags. On credited invoices, only a smaller credit flags.';
    return `If either the invoice or Nexsyis extended total is under ${baseLabel}, the line is flagged when those totals differ by ${pct}% or more. Otherwise it is flagged when they differ by ${dollarLabel} or more. ${direction}`;
};

export const AMOUNT_MISMATCH_DIRECTION_OPTIONS = [
    { value: 'both', label: 'Both directions' },
    { value: 'invoice_higher', label: 'Invoice higher only' },
] as const;

export const amountMismatchDirectionValue = (flagBothDirections: boolean | undefined): string =>
    flagBothDirections === false ? 'invoice_higher' : 'both';

export const flagBothDirectionsFromSelection = (direction: string): boolean =>
    direction !== 'invoice_higher';

export const NEXSYIS_INVOICE_REFERENCE_OPTIONS = [
    { value: 'ro', label: 'Folder Number (RO)' },
    { value: 'po', label: 'Purchase Order (PO)' },
    { value: 'both', label: 'Both' },
] as const;

export const UNMATCHED_LINE_ITEMS_NEX_SUBMIT_OPTIONS = [
    { value: 'dont_allow', label: "Don't Allow" },
    { value: 'allow_manual', label: 'Allow Manual' },
    { value: 'allow_manual_and_automatic', label: 'Allow Automatic' },
] as const;

export const NEX_LINE_QUANTITY_BASIS_OPTIONS = [
    { value: 'qtyRec', label: 'Quantity Received' },
    { value: 'qtyOnOrder', label: 'Quantity on Order' },
] as const;

export function upsertLocationDefaultTaxRate(
    entries: LocationDefaultTaxRate[],
    locationId: string,
    patch: Partial<LocationDefaultTaxRate>,
): LocationDefaultTaxRate[] {
    if (entries.some((entry) => entry.locationId === locationId)) {
        return entries.map((entry) =>
            entry.locationId === locationId ? { ...entry, ...patch } : entry,
        );
    }
    return [
        ...entries,
        {
            locationId,
            applyToReceived: false,
            applyToCredited: false,
            applyToSublet: false,
            ...patch,
        },
    ];
}

export interface InvoicingDraft {
    /** Current invoicing settings: the shared draft when dirty, else the server value. */
    invoicing: InvoicingSettingsUpdatePayload;
    /** Merges a patch into the complete subdocument and stores it on the draft. */
    updateInvoicing: (patch: Partial<InvoicingSettingsUpdatePayload>) => void;
    isSaving: boolean;
}

/**
 * Every invoicing section edits the same `invoicingSettings` subdocument, and
 * `POST /settings/update` replaces that subdocument wholesale, so writes always
 * carry the complete object rather than the changed field alone.
 */
export function useInvoicingDraft(data: InvoicingSettings): InvoicingDraft {
    const { getDomain, setDomain, isSaving } = useSettingsDraft();
    const serverValue = useMemo(() => invoicingSettingsForApi(data), [data]);
    const invoicing = getDomain('invoicingSettings', serverValue) ?? serverValue;

    const updateInvoicing = useCallback(
        (patch: Partial<InvoicingSettingsUpdatePayload>) => {
            setDomain('invoicingSettings', { ...invoicing, ...patch });
        },
        [invoicing, setDomain],
    );

    return { invoicing, updateInvoicing, isSaving };
}

interface PricingRowIdentity {
    insuranceCompanyId: string;
    supplierId: string;
}

interface NamedPricingRow extends PricingRowIdentity {
    insuranceCompanyName?: string;
    supplierName?: string;
}

export interface PricingRowNames {
    /** Re-attaches the display names the tables render to ids-only draft rows. */
    withNames: <T extends PricingRowIdentity>(rows: T[]) => Array<T & NamedPricingRow>;
    /** Keeps the names of rows added this session, before the next refetch. */
    rememberNames: (rows: NamedPricingRow[]) => void;
}

/**
 * The update payload carries ids only, so display names do not survive a round
 * trip through the draft. They are kept beside it, keyed by id: the ones the
 * API returned plus the ones the add-row modals supplied.
 */
export function usePricingRowNames(serverRows: NamedPricingRow[]): PricingRowNames {
    const [addedRows, setAddedRows] = useState<NamedPricingRow[]>([]);

    const index = useMemo(() => {
        const insuranceCompanyNames = new Map<string, string>();
        const supplierNames = new Map<string, string>();
        for (const row of [...serverRows, ...addedRows]) {
            if (row.insuranceCompanyName) {
                insuranceCompanyNames.set(row.insuranceCompanyId, row.insuranceCompanyName);
            }
            if (row.supplierName) {
                supplierNames.set(row.supplierId, row.supplierName);
            }
        }
        return { insuranceCompanyNames, supplierNames };
    }, [serverRows, addedRows]);

    const withNames = useCallback(
        <T extends PricingRowIdentity>(rows: T[]): Array<T & NamedPricingRow> =>
            rows.map((row) => ({
                ...row,
                insuranceCompanyName: index.insuranceCompanyNames.get(row.insuranceCompanyId),
                supplierName: index.supplierNames.get(row.supplierId),
            })),
        [index],
    );

    const rememberNames = useCallback((rows: NamedPricingRow[]) => {
        setAddedRows((prev) => [...prev, ...rows]);
    }, []);

    return { withNames, rememberNames };
}

export interface SupplierNames {
    supplierName: (supplierId: string) => string;
    rememberSupplierName: (vendor: FlaggedSpecialVendor) => void;
}

/** Same problem as `usePricingRowNames`, for the flagged vendor id list. */
export function useSupplierNames(serverVendors: FlaggedSpecialVendor[]): SupplierNames {
    const [addedVendors, setAddedVendors] = useState<FlaggedSpecialVendor[]>([]);

    const namesById = useMemo(() => {
        const names = new Map<string, string>();
        for (const vendor of [...serverVendors, ...addedVendors]) {
            if (vendor.supplierName) names.set(vendor.supplierId, vendor.supplierName);
        }
        return names;
    }, [serverVendors, addedVendors]);

    const supplierName = useCallback(
        (supplierId: string) => namesById.get(supplierId) ?? supplierId,
        [namesById],
    );

    const rememberSupplierName = useCallback((vendor: FlaggedSpecialVendor) => {
        setAddedVendors((prev) => [...prev, vendor]);
    }, []);

    return { supplierName, rememberSupplierName };
}
